/**
 * Zentrale Toast-API (ein Import-Pfad für die gesamte App).
 * Nutzt react-hot-toast; Host ist ausschließlich ToastSystem in App.jsx.
 *
 * - showSuccess / showError / showInfo / showWarning
 * - showLoading -> id; updateToast(id, …); dismissToast(id)
 * - promise(p, opts); handleError(err, fallbackKey)
 * - Dedupe: gleicher type+message oder dedupeKey innerhalb 3s wird unterdrückt
 * - Rate-Limit: max 3 Toasts gleichzeitig sichtbar
 * - API-Fehler-Mapping: 401/403/404/409/422/500, Netzwerk/Timeout → nutzerfreundliche Texte
 */

import React from "react";
import { toast } from "react-hot-toast";
import {
  getErrorMessage,
  getStatusFallbackMessage,
  isApiErrorCode,
  mapLegacyErrorText,
} from "../constants/errorMessages.js";

const DEDUPE_MS = 3000;
const NETWORK_DEDUPE_MS = 8000;
const MAX_VISIBLE = 3;
const DEFAULT_DURATION = 3500;
const MAX_MESSAGE_LENGTH = 220;
const INLINE_VALIDATION_RE = /(bitte\s+.+ausf(?:u|ü)llen|pflichtfeld|required|ungueltig|ungültig)/i;
const LEADING_EMOJI_RE = /^[\p{Extended_Pictographic}\uFE0F\u200D]+\s*/u;
const TYPE_EMOJI = {
  success: "✅",
  error: "⚠️",
  info: "ℹ️",
  warning: "⚠️",
  loading: "⏳",
};
const TYPE_ICON = {
  info: "ℹ️",
  warning: "⚠️",
};

const MESSAGES = {
  "auth.invalid": "Benutzername oder Passwort ist falsch.",
  "auth.forbidden": "Keine Berechtigung.",
  "file.none": "Keine Datei ausgewählt.",
  "file.too_large": "Die Datei ist zu gross. Bitte wähle eine kleinere Datei und versuche es erneut.",
  "file.type_not_allowed": "Dieser Dateityp wird nicht unterstützt. Bitte wähle eine andere Datei.",
  "net.offline": "Keine Internetverbindung.",
  "net.unreachable": "Keine Verbindung zum Server.",
  "net.timeout": "Anfrage hat zu lange gedauert.",
  "srv.error": "Ein Fehler ist aufgetreten. Bitte später erneut versuchen.",
  "ok.saved": "Gespeichert.",
  "ok.updated": "Aktualisiert.",
  "ok.deleted": "In Papierkorb verschoben.",
  "ok.deleted_hard": "Endgültig gelöscht.",
  "ok.restored": "Wiederhergestellt.",
  "err.not_in_trash": "Nur Papierkorb-Einträge können endgültig gelöscht werden.",
};

const t = (keyOrText, fallback) =>
  keyOrText in MESSAGES ? MESSAGES[keyOrText] : (keyOrText ?? fallback ?? "");

const OFFLINE_ERROR_CODES = new Set([
  "ERR_NETWORK",
  "ENOTFOUND",
  "EAI_AGAIN",
  "ECONNREFUSED",
  "ERR_INTERNET_DISCONNECTED",
]);

function isTimeoutError(err) {
  const message = String(err?.message ?? "").toLowerCase();
  const code = String(err?.code ?? "").toUpperCase();
  return code === "ECONNABORTED" || message.includes("timeout");
}

function isCanceledError(err) {
  const code = String(err?.code ?? "").toUpperCase();
  const name = String(err?.name ?? "").toLowerCase();
  const message = String(err?.message ?? "").toLowerCase();
  return (
    code.includes("CANCELED") ||
    name.includes("abort") ||
    name.includes("cancel") ||
    message.includes("abgebrochen") ||
    message.includes("canceled")
  );
}

function isOfflineLikeError(err) {
  if (typeof navigator !== "undefined" && navigator.onLine === false) return true;
  const code = String(err?.code ?? "").toUpperCase();
  const message = String(err?.message ?? "").toLowerCase();
  if (OFFLINE_ERROR_CODES.has(code)) return true;
  if (message.includes("internet disconnected")) return true;
  if (message.includes("network request failed")) return true;
  return false;
}

function isConnectionErrorWithoutResponse(err) {
  if (err?.response) return false;
  /** Axios-Interceptor nutzt buildError(): Error ohne .response, aber mit .status bei HTTP-Antwort */
  const httpStatus = err?.status ?? err?.response?.status;
  if (httpStatus != null && Number(httpStatus) > 0) return false;
  const message = String(err?.message ?? "").trim();
  const code = String(err?.code ?? "").toUpperCase();
  if (!message && !code) return false;
  if (isTimeoutError(err)) return false;
  if (isCanceledError(err)) return false;
  return true;
}

function sanitizeMessage(input) {
  const raw = String(input ?? "");
  if (!raw) return "";
  let msg = raw
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .replace(/^axioserror:\s*/i, "")
    .replace(/^error:\s*/i, "")
    .trim();
  if (!msg || msg === "[object Object]") return "";
  if (msg.length > MAX_MESSAGE_LENGTH) msg = `${msg.slice(0, MAX_MESSAGE_LENGTH - 1)}…`;
  return msg;
}

function mapCommonTechnicalMessage(err, msg) {
  const text = String(msg ?? "").toLowerCase();
  if (!text) return "";
  if (text.includes("request failed with status code")) return "";
  if (text.includes("failed to fetch") || text.includes("network error") || text.includes("load failed")) {
    return isOfflineLikeError(err) ? t("net.offline") : t("net.unreachable");
  }
  if (text.includes("unauthorized") || text.includes("token expired") || text.includes("jwt expired")) {
    return "Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.";
  }
  if (text.includes("forbidden")) return "Keine Berechtigung.";
  if (text.includes("not found")) return "Nicht gefunden.";
  if (text.includes("internal server error")) return "Serverfehler. Bitte später erneut versuchen.";
  if (text.includes("csrf")) return "Sicherheitsprüfung fehlgeschlagen. Bitte Seite neu laden.";
  if (
    text.includes("file too large") ||
    text.includes("payloadtoolarge") ||
    text.includes("payload too large") ||
    text.includes("request entity too large") ||
    text.includes("entity too large") ||
    text.includes("limit_file_size") ||
    text.includes("datei zu gross") ||
    text.includes("datei zu groß")
  ) {
    return t("file.too_large");
  }
  if (
    text.includes("unsupported media type") ||
    text.includes("invalid file type") ||
    (text.includes("file type") && text.includes("not allowed")) ||
    (text.includes("mime type") && (text.includes("not allowed") || text.includes("invalid"))) ||
    text.includes("wrong file type")
  ) {
    return t("file.type_not_allowed");
  }
  return "";
}

/**
 * Geschäfts-Code aus typischen API-Shapes ({ code }, { error: "NOT_ASSIGNED" }, verschachtelt).
 * Unterstützte Kurzcodes in `error`/`code` sind SCREAMING_SNAKE_CASE (wie vom Backend verwendet).
 */
function extractApiSemanticCode(err) {
  const data = err?.data ?? err?.response?.data;
  if (data == null) return "";

  const up = v => String(v ?? "").trim().toUpperCase();
  const screaming = v => /^[A-Z][A-Z0-9_]*$/.test(v);

  if (typeof data === "string") {
    const u = up(data);
    return screaming(u) ? u : "";
  }

  if (typeof data.code === "string" && screaming(up(data.code))) return up(data.code);

  const e = data.error;
  if (typeof e === "string") {
    const eu = up(e);
    return screaming(eu) ? eu : "";
  }
  if (e && typeof e === "object" && typeof e.code === "string" && screaming(up(e.code))) return up(e.code);

  const m = data.message;
  if (typeof m === "string" && /^NOT_ASSIGNED$/i.test(m.trim())) return "NOT_ASSIGNED";

  const det = data.detail;
  if (typeof det === "object" && det != null && typeof det.code === "string" && screaming(up(det.code))) return up(det.code);

  return "";
}

function notAssignedUserMessage(err) {
  const url = String(err?.config?.url ?? "");
  const pathOnly = url.replace(/^https?:\/\/[^/]+/i, "");
  if (/\/service(\/|$)/i.test(pathOnly)) return "Du bist diesem Serviceauftrag nicht zugewiesen.";
  if (/\/projekte(\/|$)/i.test(pathOnly)) return "Du bist diesem Projekt nicht zugewiesen.";
  return "Nur zugewiesene Benutzer dürfen diese Aktion ausführen.";
}

function formatValidationMessages(data) {
  const d = data || {};
  if (typeof d === "string") return d;
  if (d.message) return d.message;
  const errs = d.errors || d.error || d.validation || null;
  if (!errs) return null;
  if (Array.isArray(errs)) {
    return errs
      .map((e) =>
        typeof e === "string" ? e : e?.message || (e?.field ? `${e.field}: ungültig` : null)
      )
      .filter(Boolean)
      .join("\n") || null;
  }
  if (typeof errs === "object") {
    return (
      Object.entries(errs)
        .map(([k, v]) => `${k}: ${Array.isArray(v) ? v[0] : v}`)
        .join("\n") || null
    );
  }
  return null;
}

/** API-Fehler zu nutzerfreundlicher deutscher Nachricht (intern loggen, Nutzertext sauber). */
export function extractErrorMessage(err, fallbackKey = "srv.error") {
  const status = err?.status ?? err?.response?.status;
  const data = err?.data ?? err?.response?.data;

  if (isCanceledError(err)) return "";
  if (isTimeoutError(err)) return t("net.timeout");
  if (isOfflineLikeError(err)) return t("net.offline");
  if (isConnectionErrorWithoutResponse(err)) return t("net.unreachable");

  if (status === 413) return getErrorMessage("PAYLOAD_TOO_LARGE", t("file.too_large"));
  if (status === 415) return getErrorMessage("UNSUPPORTED_MEDIA_TYPE", t("file.type_not_allowed"));

  /** Semantischer API-Code (NOT_ASSIGNED, FILE_TOO_LARGE, …) */
  let semanticCode = extractApiSemanticCode(err);
  if (
    semanticCode !== "INVALID_CREDENTIALS" &&
    typeof data?.code === "string" &&
    String(data.code).trim().toUpperCase() === "INVALID_CREDENTIALS"
  ) {
    semanticCode = "INVALID_CREDENTIALS";
  }
  if (
    semanticCode !== "INVALID_CREDENTIALS" &&
    typeof err?.code === "string" &&
    String(err.code).trim().toUpperCase() === "INVALID_CREDENTIALS"
  ) {
    semanticCode = "INVALID_CREDENTIALS";
  }
  if (
    !semanticCode &&
    typeof data?.error === "string" &&
    String(data.error).trim().toUpperCase() === "NOT_ASSIGNED"
  ) {
    semanticCode = "NOT_ASSIGNED";
  }

  const rawErrStr = typeof data?.error === "string" ? String(data.error).trim() : "";
  const bareCodeFromErrorField =
    rawErrStr && semanticCode && isApiErrorCode(rawErrStr) && rawErrStr.toUpperCase() === semanticCode;

  // 1) API message (spezifischer Enduser-Text vom Backend)
  const apiMessage = sanitizeMessage(typeof data?.message === "string" ? data.message : "");
  if (apiMessage && !isApiErrorCode(apiMessage)) {
    return apiMessage;
  }

  // 2) Bekannter Code → zentrale Map (NOT_ASSIGNED: kontextspezifisch wenn möglich)
  if (semanticCode) {
    if (semanticCode === "NOT_ASSIGNED") {
      return notAssignedUserMessage(err);
    }
    if (semanticCode === "UNSUPPORTED_MEDIA_TYPE") {
      return getErrorMessage("UNSUPPORTED_MEDIA_TYPE", t("file.type_not_allowed"));
    }
    if (semanticCode === "FILE_TOO_LARGE") {
      return getErrorMessage("FILE_TOO_LARGE", t("file.too_large"));
    }
    if (semanticCode === "NO_FILE") return t("file.none");
    if (semanticCode === "NETWORK_OFFLINE") return t("net.offline");
    const mapped = getErrorMessage(semanticCode);
    if (mapped) return mapped;
  }

  // 3) err.message als Code
  const errMsgAsCode = sanitizeMessage(err?.message ?? "");
  if (errMsgAsCode && isApiErrorCode(errMsgAsCode)) {
    const mapped = getErrorMessage(errMsgAsCode);
    if (mapped) return mapped;
  }

  const msgRaw =
    (bareCodeFromErrorField ? "" : rawErrStr) ||
    (typeof data?.msg === "string" ? data.msg : "") ||
    (typeof data?.detail === "string" ? data.detail : "") ||
    (typeof data === "string" ? data : "") ||
    err?.message ||
    "";
  const msg = sanitizeMessage(msgRaw);

  const mappedTech = mapCommonTechnicalMessage(err, msg);
  if (mappedTech) return mappedTech;

  // Legacy-Freitext in data.error
  if (rawErrStr && !isApiErrorCode(rawErrStr)) {
    const legacyMapped = mapLegacyErrorText(rawErrStr);
    if (legacyMapped) return legacyMapped;
    if (msg) return msg;
  }

  // 4) HTTP-Status-Fallback
  if (status === 422 || status === 400) {
    if (msg) return msg;
    const pretty = formatValidationMessages(data);
    if (pretty) return pretty;
    return getStatusFallbackMessage(status, getErrorMessage("INVALID_INPUT"));
  }

  const statusMsg = getStatusFallbackMessage(status);
  if (statusMsg) return statusMsg;

  if (msg) return msg;

  // 5) Generischer Fallback
  return t(fallbackKey);
}

function isValidationLikeError(err) {
  const status = err?.status ?? err?.response?.status;
  if (status === 422) return true;
  const data = err?.data ?? err?.response?.data;
  const msg = data?.message ?? data?.error ?? err?.message ?? "";
  if (status === 400) {
    if (data?.errors || data?.validation) return true;
    const errField = typeof data?.error === "string" ? data.error.trim() : "";
    // API-Codes (INVALID_INPUT, TOO_OLD, …) sind keine Inline-Validierung.
    if (errField && !isApiErrorCode(errField) && INLINE_VALIDATION_RE.test(errField)) return true;
  }
  if (INLINE_VALIDATION_RE.test(String(msg))) return true;
  const errs = data?.errors || data?.validation;
  if (Array.isArray(errs) && errs.length) return true;
  if (errs && typeof errs === "object" && !Array.isArray(errs) && Object.keys(errs).length) return true;
  return false;
}

function normalizeErrorInput(messageOrError, opts = {}) {
  if (messageOrError instanceof Error || (messageOrError && typeof messageOrError === "object")) {
    const status = messageOrError?.status ?? messageOrError?.response?.status;
    const data = messageOrError?.data ?? messageOrError?.response?.data;
    const code = data?.code ?? messageOrError?.code ?? null;
    const details = data?.details ?? null;
    return {
      message: extractErrorMessage(messageOrError, opts.fallbackKey ?? "srv.error"),
      isValidation: isValidationLikeError(messageOrError),
      isCanceled: isCanceledError(messageOrError),
      meta: { status, code, details },
    };
  }
  const message = sanitizeMessage(messageOrError);
  return {
    message,
    // Validierungs-Hinweise sollen inline bleiben (kein Toast).
    isValidation: INLINE_VALIDATION_RE.test(message),
    isCanceled: false,
    meta: null,
  };
}

// —— Dedupe: key -> lastShownAt
const dedupeMap = new Map();
// —— Rate-Limit: aktive Toast-IDs (älteste zuerst entfernen bei > MAX_VISIBLE)
const activeIds = [];
const durationById = new Map();

function dedupeKey(type, message, opts = {}) {
  if (opts.dedupeKey) return String(opts.dedupeKey);
  const msg = typeof message === "string" ? message.trim() : String(message ?? "");
  if (type === "error" && (msg === t("net.offline") || msg === t("net.unreachable") || msg === t("net.timeout"))) {
    return "error::network";
  }
  return `${type}::${msg}`;
}

function formatToastMessage(type, message, opts = {}) {
  const raw = String(message ?? "").trim();
  if (!raw) return raw;
  if (opts.emoji === false) return raw.replace(LEADING_EMOJI_RE, "").trim();
  // Einheitlicher Enterprise-Style: Symbolik nur über Toast-Icon, nicht im Text.
  if (opts.forceEmoji !== true) return raw.replace(LEADING_EMOJI_RE, "").trim();
  const base = raw.replace(LEADING_EMOJI_RE, "").trim();
  const emoji = TYPE_EMOJI[type];
  if (!emoji) return base || raw;
  return `${emoji} ${base || raw}`;
}

function shouldDedupe(key, dedupeWindowMs = DEDUPE_MS) {
  const now = Date.now();
  const last = dedupeMap.get(key);
  if (last != null && now - last < dedupeWindowMs) return true;
  dedupeMap.set(key, now);
  setTimeout(() => dedupeMap.delete(key), dedupeWindowMs + 100);
  return false;
}

function trackActive(id, duration = DEFAULT_DURATION) {
  const current = activeIds.indexOf(id);
  if (current !== -1) activeIds.splice(current, 1);
  while (activeIds.length >= MAX_VISIBLE && activeIds.length > 0) {
    const old = activeIds.shift();
    toast.dismiss(old);
    const oldTimer = durationById.get(old);
    if (oldTimer) clearTimeout(oldTimer);
    durationById.delete(old);
  }
  activeIds.push(id);
  const prevTimer = durationById.get(id);
  if (prevTimer) clearTimeout(prevTimer);
  if (typeof duration === "number" && duration > 0) {
    const timerId = setTimeout(() => {
      const i = activeIds.indexOf(id);
      if (i !== -1) activeIds.splice(i, 1);
      durationById.delete(id);
    }, duration);
    durationById.set(id, timerId);
  }
}

function renderActionToast(type, message, opts = {}) {
  const { action } = opts;
  const label = action?.label || "Erneut versuchen";
  const onAction = typeof action?.onClick === "function" ? action.onClick : null;
  if (!onAction) return null;
  const palette = {
    border: "1px solid rgba(148,163,184,0.35)",
    background: "rgba(15,23,42,0.95)",
    text: "#e2e8f0",
    shadow: "0 8px 24px rgba(0,0,0,0.35)",
    actionBg: type === "error" ? "rgba(239,68,68,0.18)" : "rgba(59,130,246,0.18)",
    actionText: "#f8fafc",
  };

  return toast.custom(
    (instance) =>
      React.createElement(
        "div",
        {
          role: "status",
          "aria-live": "polite",
          style: {
            display: "flex",
            alignItems: "center",
            gap: "10px",
            padding: "12px 14px",
            borderRadius: "12px",
            border: "1px solid rgba(148,163,184,0.35)",
            background: palette.background,
            color: palette.text,
            boxShadow: palette.shadow,
            maxWidth: "min(100%, 420px)",
          },
        },
        React.createElement(
          "div",
          { style: { flex: 1, overflowWrap: "break-word", wordBreak: "break-word" } },
          String(message ?? "")
        ),
        React.createElement(
          "button",
          {
            type: "button",
            onClick: () => {
              toast.dismiss(instance.id);
              onAction();
            },
            onKeyDown: (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                toast.dismiss(instance.id);
                onAction();
              }
            },
            style: {
              border: "1px solid rgba(148,163,184,0.5)",
              background: palette.actionBg,
              color: palette.actionText,
              borderRadius: "8px",
              padding: "6px 10px",
              fontSize: "12px",
              fontWeight: 600,
              cursor: "pointer",
            },
            "aria-label": label,
          },
          label
        )
      ),
    opts
  );
}

function show(type, message, opts = {}) {
  const rawMsg = typeof message === "string" ? message : t(message, "");
  const msg = formatToastMessage(type, rawMsg, opts);
  if (!msg) return null;
  const key = dedupeKey(type, msg, opts);
  const isNetworkError =
    type === "error" &&
    (msg === t("net.offline") || msg === t("net.unreachable") || msg === t("net.timeout"));
  const dedupeWindowMs = opts.dedupeWindowMs ?? (isNetworkError ? NETWORK_DEDUPE_MS : DEDUPE_MS);
  if (opts.dedupe !== false && shouldDedupe(key, dedupeWindowMs)) return null;

  const duration = opts.duration ?? DEFAULT_DURATION;
  const { action, ...rest } = opts;
  const options = { duration, ...rest };
  if ((type === "info" || type === "warning") && options.icon == null) {
    options.icon = TYPE_ICON[type];
  }

  let toastId;
  if (action?.onClick) {
    toastId = renderActionToast(type, msg, { ...options, action });
  } else if (type === "success") toastId = toast.success(msg, options);
  else if (type === "error") toastId = toast.error(msg, options);
  else if (type === "loading") toastId = toast.loading(msg, options);
  else if (type === "info" || type === "warning") toastId = toast(msg, { ...options, type });
  else if (type === "custom") toastId = toast(msg, options);
  else toastId = toast(msg, { ...options, type });

  const rid = toastId ?? options.id;
  if (rid != null) trackActive(rid, type === "loading" ? null : duration);
  return rid;
}

export function showSuccess(message, opts = {}) {
  return show("success", message, opts);
}

export function showError(messageOrError, opts = {}) {
  const normalized = normalizeErrorInput(messageOrError, opts);
  const allowValidationToast = opts.validationToast === true;
  if (normalized.isCanceled) return null;
  if (normalized.isValidation && !allowValidationToast) return null;
  if (normalized.meta?.status || normalized.meta?.code || normalized.meta?.details) {
    // Interne Details bleiben in der Konsole; Nutzer erhalten nur saubere Meldungen.
    console.error("[toast:error-meta]", normalized.meta);
  }
  return show("error", normalized.message, opts);
}

export function showInfo(message, opts = {}) {
  return show("info", message, opts);
}

export function showWarning(message, opts = {}) {
  return show("warning", message, opts);
}

function forbiddenMessage(reason = "default") {
  const key = String(reason || "default").toLowerCase();
  if (key === "admin_area") return "Admin-Bereich – keine Berechtigung.";
  if (key === "not_assigned") return "Nur zugewiesene Benutzer dürfen diese Aktion ausführen.";
  if (key === "restricted_role") return "Keine Berechtigung für diese Aktion.";
  return "Keine Berechtigung.";
}

export function showForbidden(reason = "default", opts = {}) {
  const { level = "warning", ...rest } = opts || {};
  const msg = forbiddenMessage(reason);
  if (String(level).toLowerCase() === "error") return showError(msg, rest);
  return showWarning(msg, rest);
}

/** Gibt Toast-ID zurück zum späteren updateToast/dismissToast. */
export function showLoading(message, opts = {}) {
  const rawMsg = typeof message === "string" ? message : t(message, "Bitte warten…");
  const msg = formatToastMessage("loading", rawMsg, opts);
  if (opts.dedupe !== false) {
    const key = dedupeKey("loading", msg, opts);
    if (shouldDedupe(key)) return opts.id ?? null;
  }
  const id = toast.loading(msg, opts);
  if (id != null) trackActive(id, null);
  return id;
}

/** Bestehenden Toast aktualisieren (z. B. Loading → Success). */
export function updateToast(id, payload = {}) {
  if (id == null) return;
  const { type = "success", message, duration = DEFAULT_DURATION, ...rest } = payload;
  const msg = message != null ? formatToastMessage(type, String(message), rest) : undefined;
  if (type === "success") toast.success(msg, { id, duration, ...rest });
  else if (type === "error") toast.error(msg, { id, duration, ...rest });
  else if (type === "loading") toast.loading(msg, { id, ...rest });
  else toast(msg, { id, type, duration, ...rest });
  trackActive(id, type === "loading" ? null : duration);
}

export function dismissToast(id) {
  if (id != null) toast.dismiss(id);
  const i = activeIds.indexOf(id);
  if (i !== -1) activeIds.splice(i, 1);
  const tId = durationById.get(id);
  if (tId) clearTimeout(tId);
  durationById.delete(id);
}

export function dismissAllToasts() {
  activeIds.length = 0;
  durationById.clear();
  toast.dismiss();
}

/** Promise mit Loading/Success/Error-Toast (ein Feedback pro Aktion). */
export function promise(p, opts = {}) {
  return runAction(() => p, opts);
}

/**
 * Orchestriert eine User-Aktion als genau einen Feedback-Flow:
 * loading -> success/error (optional Retry-Action).
 */
export async function runAction(actionOrPromise, opts = {}) {
  const loading = opts.loading ?? "Bitte warten…";
  const successOpt = opts.success ?? "ok.saved";
  const errorKey = opts.error ?? "srv.error";
  const loadingText = typeof loading === "string" ? loading : t(loading, "Bitte warten…");
  const flowKey = opts.dedupeKey ?? undefined;
  const loadingOpts = {
    id: opts.id,
    duration: opts.loadingDuration,
    position: opts.position,
    style: opts.style,
    className: opts.className,
    icon: opts.icon,
    dedupe: opts.dedupe,
    dedupeKey: flowKey ? `${flowKey}:loading` : undefined,
  };

  const loadingId = showLoading(loadingText, loadingOpts);

  try {
    const result =
      typeof actionOrPromise === "function" ? await actionOrPromise() : await actionOrPromise;
    const successText =
      typeof successOpt === "function" ? successOpt(result) : t(result?.message ?? successOpt);
    updateToast(loadingId, {
      type: "success",
      message: successText,
      duration: opts.duration ?? DEFAULT_DURATION,
      dedupeKey: flowKey ? `${flowKey}:done` : undefined,
    });
    return result;
  } catch (err) {
    const isValidation = isValidationLikeError(err);
    if (isValidation && opts.validationToast !== true) {
      dismissToast(loadingId);
      throw err;
    }

    const retryAction =
      typeof opts.retry === "function"
        ? {
            label: opts.retryLabel || "Erneut versuchen",
            onClick: () => {
              try {
                opts.retry();
              } catch (retryErr) {
                console.error("[toast:retry-failed]", retryErr);
              }
            },
          }
        : undefined;

    dismissToast(loadingId);
    showError(err, {
      fallbackKey: errorKey,
      dedupeKey: flowKey ? `${flowKey}:error` : undefined,
      action: retryAction,
      duration: opts.errorDuration ?? 4200,
      validationToast: opts.validationToast === true,
    });
    throw err;
  }
}

/** Fehler als Toast anzeigen (z. B. nach catch). */
export function handleError(err, fallbackKeyOrOpts = "srv.error", maybeOpts = {}) {
  const opts =
    fallbackKeyOrOpts && typeof fallbackKeyOrOpts === "object"
      ? fallbackKeyOrOpts
      : { ...maybeOpts, fallbackKey: fallbackKeyOrOpts };
  const fallbackKey = opts.fallbackKey ?? "srv.error";
  return showError(extractErrorMessage(err, fallbackKey), opts);
}

// —— Kompatibilität: ToastKit-ähnliches Objekt (ein Import-Pfad)
export const toastApi = {
  success: (msg, o) => showSuccess(typeof msg === "string" ? msg : t(msg), o),
  error: (msg, o) =>
    typeof msg === "string" || msg == null
      ? showError(msg ?? t("srv.error"), o)
      : showError(msg, o),
  info: (msg, o) => showInfo(typeof msg === "string" ? msg : t(msg), o),
  warn: (msg, o) => showWarning(typeof msg === "string" ? msg : t(msg), o),
  forbidden: (reason, o) => showForbidden(reason, o),
  loading: (msg, o) => showLoading(typeof msg === "string" ? msg : t(msg, "Bitte warten…"), o),
  dismiss: dismissToast,
  dismissAll: dismissAllToasts,
  promise,
  runAction,
  handleError,
};

export default toastApi;
